import React, { useRef, useEffect } from 'react';
import { DownOutlined } from '@ant-design/icons';
import Image from 'next/image';
import Link from 'next/link';

interface DropdownProps {
  imageUrl?: string;
  fullName?: string;
  label?: string;
  menuItems: {
    key: string;
    label: string;
    href?: string;
    onClick?: () => void;
  }[];
  isOpen: boolean;
  showIcon?: boolean;
  onToggle: () => void;
  onClose: () => void;
  width?: string;
  className?: string;
}

const Dropdown: React.FC<DropdownProps> = ({
  imageUrl,
  fullName,
  label,
  menuItems,
  isOpen,
  showIcon = true,
  onToggle,
  onClose,
  width = 'w-48',
  className = '',
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const shouldShowIcon = showIcon || !(imageUrl || fullName || label);

  return (
    <div ref={wrapperRef} className={`relative inline-block ${className}`}>
      <div className="flex items-center cursor-pointer" onClick={onToggle}>
        {imageUrl && (
          <Image
            src={imageUrl || '/default-profile.png'}
            alt="Profile Image"
            width={40}
            height={40}
            className="rounded-full"
          />
        )}
        {fullName && <span className="ml-2 text-lg md:flex">{fullName}</span>}
        {label && <span className="text-lg underline">{label}</span>}
        {shouldShowIcon && <DownOutlined className="ml-2 text-black" />}
      </div>
      {isOpen && (
        <div className={`absolute p-5 right-0 bg-white rounded-lg shadow-lg z-10 ${width}`}>
          {menuItems.map((item) =>
            item.href ? (
              item.href.startsWith('http') ? (
                <a
                  key={item.key}
                  href={item.href}
                  className="block mb-3 last:mb-0 text-sm md:text-lg font-medium hover:underline rounded-lg cursor-pointer"
                  onClick={onClose}
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.key}
                  href={item.href}
                  className="block mb-3 last:mb-0 text-sm md:text-lg font-medium hover:underline rounded-lg cursor-pointer"
                  onClick={onClose}
                  passHref
                  prefetch={true}
                >
                  {item.label}
                </Link>
              )
            ) : (
              <button
                key={item.key}
                className="block mb-3 last:mb-0 text-lg hover:underline rounded-lg font-medium cursor-pointer w-full text-left"
                onClick={() => {
                  if (item.onClick) {
                    item.onClick();
                  }
                  onClose();
                }}
              >
                {item.label}
              </button>
            ),
          )}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
